import { Suspense } from "react";
import { Routes } from "react-router-dom";
import { Loader } from "@/components/loader/loader";
import { ROLES } from "@/constants/constants";
import { usePermission } from "@/hooks/usePermissions";
import SidebarStatusProvider from "@/store/sidebar-status-context";
import { ToastProvider } from "../../components/toast/toast-provider";
import { Layout } from "../../layout/Layout";
import { LoaderWrapper } from "./main.components";
import { useMainAppSetup } from "./main.hooks";
import type { MainProps } from "./main.types";
import {
	generateDeviceFingerprintRoutes,
	generateFullSubscriptionRoutes,
} from "./main.utils";

/**
 * Main application component that handles routing based on user subscriptions
 * and feature flags. It initializes the user context and renders appropriate
 * routes based on the user's subscription level.
 */
export const Main = ({ userData }: MainProps) => {
	const { hasAccess: hasAdminAccess } = usePermission({ role: ROLES.ADMIN });
	const {
		subscriptions,
		hasDeviceFingerprintOnly,
		defaultRoute,
		flagList,
		isLoading,
	} = useMainAppSetup(userData);

	// Wait for subscriptions to load before rendering routes
	// This prevents route flashing and incorrect redirects
	if (subscriptions.length === 0) {
		return null;
	}

	// Generate routes based on subscription type
	const routes = hasDeviceFingerprintOnly
		? generateDeviceFingerprintRoutes(hasAdminAccess, isLoading)
		: generateFullSubscriptionRoutes(
				hasAdminAccess,
				flagList,
				subscriptions,
				defaultRoute,
				isLoading,
			);

	return (
		<ToastProvider>
			<SidebarStatusProvider>
				<Layout>
					<Suspense
						fallback={
							<LoaderWrapper>
								<Loader size="50" />
							</LoaderWrapper>
						}
					>
						<Routes>{routes}</Routes>
					</Suspense>
				</Layout>
			</SidebarStatusProvider>
		</ToastProvider>
	);
};

export default Main;
